/**
 * Extracts FAQ question/answer pairs from markdown content.
 * Looks for a section starting with "## FAQ" or "## Frequently Asked Questions",
 * then parses ### headings as questions and following paragraphs as answers.
 */
export interface FaqItem {
  question: string;
  answer: string;
}

export function extractFaqs(markdown: string): FaqItem[] {
  const faqs: FaqItem[] = [];

  // Find the FAQ section (## FAQ or ## Frequently Asked Questions)
  const faqMatch = markdown.match(
    /^##\s+(?:FAQ|Frequently Asked Questions)\s*$/m
  );
  if (!faqMatch || faqMatch.index === undefined) return faqs;

  // Get content from FAQ heading to next ## heading or --- or end
  const afterFaq = markdown.slice(faqMatch.index + faqMatch[0].length);
  const sectionEnd = afterFaq.search(/^##\s|^---\s*$/m);
  const faqSection = sectionEnd === -1 ? afterFaq : afterFaq.slice(0, sectionEnd);

  // Split by ### headings (questions)
  const parts = faqSection.split(/^###\s+/m);

  for (let i = 1; i < parts.length; i++) {
    const part = parts[i].trim();
    const newlineIdx = part.indexOf('\n');
    if (newlineIdx === -1) continue;

    const question = part.slice(0, newlineIdx).trim();
    const answer = part
      .slice(newlineIdx + 1)
      .trim()
      // Strip markdown links but keep text
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      // Strip bold/italic markers
      .replace(/\*{1,2}([^*]+)\*{1,2}/g, '$1')
      // Collapse multiple newlines
      .replace(/\n{2,}/g, ' ')
      .replace(/\n/g, ' ')
      .trim();

    if (question && answer) {
      faqs.push({ question, answer });
    }
  }

  return faqs;
}

/**
 * Generates a FAQPage JSON-LD object from extracted FAQ items.
 */
export function buildFaqJsonLd(faqs: FaqItem[]) {
  if (faqs.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}
